// inv/src/components/ui/hoverHint.jsx
import { useEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';

const SIDES = ['top', 'right', 'bottom', 'left'];

function useAutoPosition({ anchorRef, open }) {
  const [coords, setCoords] = useState({ x: 0, y: 0, side: 'top' });

  useEffect(() => {
    if (!open || !anchorRef.current) return;

    const update = () => {
      const a = anchorRef.current;
      const rect = a.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const estW = Math.min(320, vw - 16);
      const estH = 100;

      const free = {
        top: rect.top,
        bottom: vh - rect.bottom,
        left: rect.left,
        right: vw - rect.right,
      };

      let side = 'top';
      for (const s of SIDES) {
        if (s === 'top' && free.top >= estH) { side = s; break; }
        if (s === 'right' && free.right >= estW) { side = s; break; }
        if (s === 'bottom' && free.bottom >= estH) { side = s; break; }
        if (s === 'left' && free.left >= estW) { side = s; break; }
      }

      const gap = 8;
      let x = rect.left + rect.width / 2;
      let y = rect.top + rect.height / 2;

      if (side === 'top') y = rect.top - gap;
      if (side === 'bottom') y = rect.bottom + gap;
      if (side === 'left') x = rect.left - gap;
      if (side === 'right') x = rect.right + gap;

      setCoords({ x, y, side });
    };

    update();

    let raf;
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    const ro = new ResizeObserver(onScroll);
    ro.observe(anchorRef.current);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [open, anchorRef]);

  return { coords, setCoords };
}

export default function HoverHint({
  children,
  message,
  delay = 120,
  hideDelay = 120,
  interactive = true,
  className = '',
}) {
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState(false);
  const { coords, setCoords } = useAutoPosition({ anchorRef, open });
  const tipId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        scheduleHide(0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const clearTimers = () => {
    if (showTimerRef.current) { clearTimeout(showTimerRef.current); showTimerRef.current = null; }
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
  };

  const scheduleShow = (ms = delay) => {
    if (open && armed) return;
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
    showTimerRef.current = setTimeout(() => {
      setOpen(true);
      requestAnimationFrame(() => setArmed(true));
    }, ms);
  };

  const scheduleHide = (ms = hideDelay) => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setArmed(false);
      setTimeout(() => setOpen(false), 100);
    }, Math.max(0, ms));
  };

  useEffect(() => {
    if (!open || !tipRef.current || !anchorRef.current) return;
    const tip = tipRef.current;
    const a = anchorRef.current;
    const rect = a.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const vw = window.innerWidth;

    const gap = 8;
    let x = coords.x;
    let y = coords.y;

    if (coords.side === 'top') {
      x = rect.left + rect.width / 2 - tipRect.width / 2;
      y = rect.top - gap - tipRect.height;
    } else if (coords.side === 'bottom') {
      x = rect.left + rect.width / 2 - tipRect.width / 2;
      y = rect.bottom + gap;
    } else if (coords.side === 'left') {
      x = rect.left - gap - tipRect.width;
      y = rect.top + rect.height / 2 - tipRect.height / 2;
    } else if (coords.side === 'right') {
      x = rect.right + gap;
      y = rect.top + rect.height / 2 - tipRect.height / 2;
    }

    const margin = 8;
    x = Math.max(margin, Math.min(x, vw - tipRect.width - margin));

    setCoords(prev => ({ ...prev, x, y }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, coords.side]);

  useEffect(() => {
    return () => clearTimers();
  }, []);

  const child = Array.isArray(children) ? children[0] : children;
  const cloned = (
    <span
      ref={anchorRef}
      className="inline-flex items-center"
      onMouseEnter={() => scheduleShow()}
      onMouseLeave={() => scheduleHide()}
      onFocus={() => scheduleShow(0)}
      onBlur={() => scheduleHide()}
      tabIndex={0}
      aria-describedby={open ? tipId : undefined}
    >
      {child}
    </span>
  );

  return (
    <>
      {cloned}
      {open && createPortal(
        <div
          ref={tipRef}
          role="tooltip"
          id={tipId}
          style={{ position: 'fixed', left: coords.x, top: coords.y, zIndex: 50 }}
          className={[
            'pointer-events-auto select-none',
            'rounded-2xl shadow-lg px-3 py-2',
            'bg-custom3 text-white text-xs',
            'max-w-sm leading-snug',
            armed ? 'opacity-100 translate-y-0 transition-opacity duration-100' : 'opacity-0',
            'before:absolute before:content-[""] before:w-2.5 before:h-2.5 before:bg-zinc-900 before:rotate-45',
            coords.side === 'top' ? 'before:bottom-[-5px] before:left-1/2 before:-translate-x-1/2' : '',
            coords.side === 'bottom' ? 'before:top-[-5px]    before:left-1/2 before:-translate-x-1/2' : '',
            coords.side === 'left' ? 'before:right-[-5px]  before:top-1/2 before:-translate-y-1/2' : '',
            coords.side === 'right' ? 'before:left-[-5px]   before:top-1/2 before:-translate-y-1/2' : '',
            className,
          ].join(' ')}
          onMouseEnter={() => {
            if (!interactive) return;
            if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
          }}
          onMouseLeave={() => {
            if (!interactive) return;
            scheduleHide();
          }}
        >
          {message}
        </div>,
        document.body
      )}
    </>
  );
}
